import { ICallAndSetData } from "@/services/api-service/interface";
import { getSetApiData } from "@/services/api-service/get-set-api-data";
import { EHttpMethod } from "@/services/api-service/enums";

const BASE = "/v1/gomake-ai-service/ai-credits";

export type AiCreditsDateParams = {
  from?: string;
  to?: string;
};

function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return "";
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && String(v).length > 0)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

const getAiCreditsSummaryApi: ICallAndSetData = async (callApi, setState) => {
  return getSetApiData(callApi, EHttpMethod.GET, `${BASE}/summary`, setState, {}, false);
};

const getAiCreditsUsageOverTimeApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: AiCreditsDateParams
) => {
  return getSetApiData(
    callApi,
    EHttpMethod.GET,
    `${BASE}/usage-over-time${qs(params)}`,
    setState,
    {},
    false
  );
};

const getAiCreditsUsageByFeatureApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: AiCreditsDateParams
) => {
  return getSetApiData(
    callApi,
    EHttpMethod.GET,
    `${BASE}/usage-by-feature${qs(params)}`,
    setState,
    {},
    false
  );
};

const getAiCreditsUsageByAgentApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: AiCreditsDateParams
) => {
  return getSetApiData(
    callApi,
    EHttpMethod.GET,
    `${BASE}/usage-by-agent${qs(params)}`,
    setState,
    {},
    false
  );
};

const getAiCreditsTransactionsApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: AiCreditsDateParams & {
    type?: string;
    page?: number;
    pageSize?: number;
  }
) => {
  return getSetApiData(
    callApi,
    EHttpMethod.GET,
    `${BASE}/transactions${qs(params as any)}`,
    setState,
    {},
    false
  );
};

const getAiCreditsOperationDetailApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: { operationId: string }
) => {
  const id = params?.operationId || "";
  return getSetApiData(
    callApi,
    EHttpMethod.GET,
    `${BASE}/operations/${encodeURIComponent(id)}`,
    setState,
    {},
    false
  );
};

const checkAiCreditsGateApi: ICallAndSetData = async (callApi, setState) => {
  return getSetApiData(
    callApi,
    EHttpMethod.POST,
    `${BASE}/gate`,
    setState,
    {},
    false
  );
};

const getAdminAiCreditsAccountApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: { printHouseId: string }
) => {
  const ph = params?.printHouseId || "";
  return getSetApiData(
    callApi,
    EHttpMethod.GET,
    `/v1/gomake-ai-service/admin/ai-credits/${encodeURIComponent(ph)}`,
    setState,
    {},
    true
  );
};

const adminGrantAiCreditsApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: {
    printHouseId: string;
    amount: number;
    reason?: string;
    note?: string;
    enableEnforcement?: boolean;
  }
) => {
  const ph = params?.printHouseId || "";
  return getSetApiData(
    callApi,
    EHttpMethod.POST,
    `/v1/gomake-ai-service/admin/ai-credits/${encodeURIComponent(ph)}/grant`,
    setState,
    {
      amount: params?.amount,
      reason: params?.reason,
      note: params?.note,
      enableEnforcement: params?.enableEnforcement,
    },
    true
  );
};

const adminAdjustAiCreditsApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: { printHouseId: string; amount: number; reason?: string; note?: string }
) => {
  const ph = params?.printHouseId || "";
  return getSetApiData(
    callApi,
    EHttpMethod.POST,
    `/v1/gomake-ai-service/admin/ai-credits/${encodeURIComponent(ph)}/adjust`,
    setState,
    {
      amount: params?.amount,
      reason: params?.reason,
      note: params?.note,
    },
    true
  );
};

const adminSetAiCreditsStatusApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: {
    printHouseId: string;
    status: string;
    enableEnforcement?: boolean;
  }
) => {
  const ph = params?.printHouseId || "";
  return getSetApiData(
    callApi,
    EHttpMethod.POST,
    `/v1/gomake-ai-service/admin/ai-credits/${encodeURIComponent(ph)}/status`,
    setState,
    {
      status: params?.status,
      enableEnforcement: params?.enableEnforcement,
    },
    true
  );
};

const getAiCreditPackagesApi: ICallAndSetData = async (callApi, setState) => {
  return getSetApiData(callApi, EHttpMethod.GET, `${BASE}/packages`, setState, {}, false);
};

const createAiCreditCheckoutApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: { packageId: string; autoRenew?: boolean }
) => {
  return getSetApiData(
    callApi,
    EHttpMethod.POST,
    `${BASE}/checkout`,
    setState,
    {
      packageId: params?.packageId,
      autoRenew: params?.autoRenew === true,
    },
    false
  );
};

const getAiCreditPurchaseApi: ICallAndSetData = async (
  callApi,
  setState,
  params?: { purchaseId: string }
) => {
  const id = params?.purchaseId || "";
  return getSetApiData(
    callApi,
    EHttpMethod.GET,
    `${BASE}/purchases/${encodeURIComponent(id)}`,
    setState,
    {},
    false
  );
};

const getAiCreditCommercialApi: ICallAndSetData = async (callApi, setState) => {
  return getSetApiData(callApi, EHttpMethod.GET, `${BASE}/commercial`, setState, {}, false);
};

const disableAiCreditAutoRenewApi: ICallAndSetData = async (callApi, setState) => {
  return getSetApiData(
    callApi,
    EHttpMethod.POST,
    `${BASE}/auto-renew/off`,
    setState,
    {},
    false
  );
};

export {
  getAiCreditsSummaryApi,
  getAiCreditsUsageOverTimeApi,
  getAiCreditsUsageByFeatureApi,
  getAiCreditsUsageByAgentApi,
  getAiCreditsTransactionsApi,
  getAiCreditsOperationDetailApi,
  checkAiCreditsGateApi,
  getAdminAiCreditsAccountApi,
  adminGrantAiCreditsApi,
  adminAdjustAiCreditsApi,
  adminSetAiCreditsStatusApi,
  getAiCreditPackagesApi,
  createAiCreditCheckoutApi,
  getAiCreditPurchaseApi,
  getAiCreditCommercialApi,
  disableAiCreditAutoRenewApi,
};
